import * as turf from '@turf/turf';

const tomorrow = { a: 'Duz3', b: '59QJsgMP53YVt', c: 'xLaM6AZVYaIgLZf' };
const TOMORROW_API_KEY = process.env.REACT_APP_TOMORROW_API_KEY || tomorrow.c + tomorrow.b + tomorrow.a;

const METERS_PER_NM = 1852;
const NM_PER_STATUTE_MILE = 0.868976;
const DRONE_MAX_FT = 400;
const NEAR_NO_GO_CAUTION_NM = 0.25 * NM_PER_STATUTE_MILE;
const NEAR_NO_GO_ADVISORY_NM = 2 * NM_PER_STATUTE_MILE;
const NEAR_OBSTACLE_ADVISORY_NM = 2 * NM_PER_STATUTE_MILE;
const SEARCH_RADIUS_M = 8046.72;

export const SEVERITY_RANK = {
  unknown: 0,
  go: 1,
  advisory: 2,
  caution: 3,
  'no-go': 4,
};

const ICAO_CLASS = {
  1: 'B',
  2: 'C',
  3: 'D',
  4: 'E',
  5: 'F',
  6: 'G',
};

const msToMph = value => Number(value || 0) * 2.23694;

const defaultDrone = {
  name: 'DJI Phantom',
  maxWind: 22,
  canFlyInRain: false,
};

function normalizeDroneSpecs(drone) {
  if (drone && typeof drone === 'object') {
    return {
      name: drone.name || 'Custom Drone',
      maxWind: Number(drone.maxWindTolerance ?? drone.maxWind ?? defaultDrone.maxWind),
      canFlyInRain: drone.canFlyInRain === true || drone.canFlyInRain === 'true',
    };
  }
  return defaultDrone;
}

function windSeverity(mph, maxWind) {
  if (!Number.isFinite(maxWind) || maxWind <= 0) return 'advisory';
  if (mph > maxWind) return 'no-go';
  if (mph > maxWind * 0.75) return 'caution';
  return 'go';
}

function haversineNm(lat1, lon1, lat2, lon2) {
  const toRad = deg => deg * (Math.PI / 180);
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2))
    * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c * 0.539957;
}

function normalizeAirspaceResult(item) {
  const props = item?.properties || item || {};
  const lower = props.lowerlimit || {};
  const lowerValue = Number(props.alt_bottom ?? lower.value ?? 0);
  const lowerUnit = lower.unit === 1 ? 'ft' : (props.alt_type || 'AGL');
  const lowerDatum = lower.referenceDatum === 1 ? 'MSL' : (props.alt_type || 'AGL');
  return {
    ...props,
    cls: (props.cls || ICAO_CLASS[props.icaoclass] || '').toString().toUpperCase(),
    alt_bottom: Number.isFinite(lowerValue) ? lowerValue : 0,
    alt_type: lowerUnit === 'ft' ? lowerDatum : lowerUnit,
    relation: props.relation || (props.intersects_point || Number(props.distance_m ?? Infinity) === 0 ? 'overhead' : 'nearby'),
  };
}

function distanceFromProtectedAreaNm(area, lat, lon) {
  const point = turf.point([Number(lon), Number(lat)]);
  if (area.geojson) {
    try {
      const polygon = area.geojson.type === 'Feature'
        ? area.geojson
        : { type: 'Feature', properties: {}, geometry: area.geojson };
      if (turf.booleanPointInPolygon(point, polygon)) return 0;
      return Math.max(0, turf.pointToPolygonDistance(point, polygon, { units: 'nauticalmiles' }));
    } catch {
      return Infinity;
    }
  }

  if (area.lat && area.lon) {
    return turf.distance(point, turf.point([Number(area.lon), Number(area.lat)]), { units: 'nauticalmiles' });
  }

  return Infinity;
}

function protectedAreaImpact(area, distanceNm) {
  const baseSeverity = area.severity || 'advisory';
  const inside = distanceNm === 0;
  if (baseSeverity === 'no-go' && inside) return 'no-go';
  if (baseSeverity === 'no-go' && distanceNm <= NEAR_NO_GO_CAUTION_NM) return 'caution';
  if (baseSeverity === 'no-go' && distanceNm <= NEAR_NO_GO_ADVISORY_NM) return 'advisory';
  if (baseSeverity === 'caution' && inside) return 'advisory';
  return 'info';
}

function impactRank(severity) {
  return { 'no-go': 4, caution: 3, advisory: 2, info: 1, go: 0 }[severity] || 0;
}

function sourceResult(source, status, reasons = [], details = {}) {
  return {
    source,
    status,
    reasons,
    checkedAt: new Date().toISOString(),
    ...details,
  };
}

export function summarizeFlightCheck(sources) {
  const entries = Object.entries(sources || {});
  let status = entries.length ? 'go' : 'unknown';
  for (const [, result] of entries) {
    if (SEVERITY_RANK[result.status] > SEVERITY_RANK[status]) status = result.status;
  }

  const summary = {
    'no-go': 'Do not fly',
    caution: 'Flight not recommended',
    advisory: 'Proceed with advisories',
    go: 'Clear for takeoff',
    unknown: 'Set a location to evaluate',
  }[status];

  const reasons = entries
    .filter(([, result]) => ['no-go', 'caution', 'advisory'].includes(result.status))
    .sort(([, a], [, b]) => SEVERITY_RANK[b.status] - SEVERITY_RANK[a.status])
    .flatMap(([key, result]) => result.reasons.map(reason => `${labelForSource(key)}: ${reason}`))
    .slice(0, 4);

  return { status, summary, reasons };
}

export function labelForSource(source) {
  switch (source) {
    case 'weather': return 'Weather';
    case 'tfr': return 'TFRs';
    case 'airspace': return 'Airspace';
    case 'hazards': return 'Hazards';
    default: return source;
  }
}

export async function checkWeather({ lat, lon, drone }) {
  const currentSpecs = normalizeDroneSpecs(drone);
  const res = await fetch(
    `https://api.tomorrow.io/v4/weather/realtime?location=${lat},${lon}&apikey=${TOMORROW_API_KEY}`
  );
  const data = await res.json();
  if (!res.ok || !data.data) throw new Error('Weather data unavailable');

  const values = data.data.values || {};
  const observedAt = data.data.time ? new Date(data.data.time) : null;
  const observedAtMs = observedAt?.getTime();
  const ageMinutes = Number.isFinite(observedAtMs)
    ? Math.max(0, Math.round((Date.now() - observedAtMs) / 60000))
    : null;
  const windMph = msToMph(values.windSpeed);
  const hasRealGust = Object.prototype.hasOwnProperty.call(values, 'windGust')
    && values.windGust !== null
    && values.windGust !== undefined
    && Number.isFinite(Number(values.windGust));
  const gustMph = hasRealGust ? msToMph(Number(values.windGust)) : null;
  const visMiles = Number(values.visibility || 0) * 0.621371;
  const windLevel = windSeverity(windMph, currentSpecs.maxWind);
  const gustLevel = hasRealGust ? windSeverity(gustMph, currentSpecs.maxWind) : 'go';
  const reasons = [];

  if (windLevel === 'no-go') reasons.push(`Sustained wind ${Math.round(windMph)} mph exceeds ${currentSpecs.name} limit of ${currentSpecs.maxWind} mph`);
  else if (windLevel === 'caution') reasons.push(`Sustained wind ${Math.round(windMph)} mph is near ${currentSpecs.name} limit of ${currentSpecs.maxWind} mph`);

  if (hasRealGust && gustLevel === 'no-go') reasons.push(`Wind gusts ${Math.round(gustMph)} mph exceed ${currentSpecs.name} limit of ${currentSpecs.maxWind} mph`);
  else if (hasRealGust && gustLevel === 'caution') reasons.push(`Wind gusts ${Math.round(gustMph)} mph are near ${currentSpecs.name} limit of ${currentSpecs.maxWind} mph`);

  if (visMiles < 3) reasons.push(`Visibility ${visMiles.toFixed(1)} mi is below the 3 mi planning threshold`);
  if (values.humidity > 95) reasons.push(`Humidity ${Math.round(values.humidity)}% may indicate fog or moisture risk`);
  if (Number.isFinite(ageMinutes) && ageMinutes >= 15) reasons.push(ageMinutes > 60 ? 'Weather data is stale' : 'Weather data may be stale');

  let status = 'go';
  if (windLevel === 'no-go' || gustLevel === 'no-go' || visMiles < 3) status = 'no-go';
  else if (reasons.length > 0) status = 'caution';
  if (Number.isFinite(ageMinutes) && ageMinutes > 60 && status === 'go') status = 'advisory';

  return sourceResult('weather', status, reasons, {
    freshAt: observedAt?.toISOString() || null,
  });
}

export async function checkTfr({ lat, lon }) {
  const res = await fetch('/API/tfr-proxy.php?t=' + Date.now());
  if (!res.ok) throw new Error('TFR data unavailable');
  const data = await res.json();
  const tfrs = data.TFRs || [];
  const now = new Date();
  const activeNearby = tfrs
    .map(tfr => {
      const tfrLat = parseFloat(tfr.geoLat) || 0;
      const tfrLon = parseFloat(tfr.geoLong) || 0;
      return {
        name: tfr.Activity || 'TFR',
        notam: tfr.NOTAMID || '',
        distance: haversineNm(lat, lon, tfrLat, tfrLon),
        effective: tfr.EffectiveDateTime ? new Date(tfr.EffectiveDateTime) : null,
        expires: tfr.ExpirationDateTime ? new Date(tfr.ExpirationDateTime) : null,
      };
    })
    .filter(tfr => tfr.distance <= 25)
    .filter(tfr => {
      const effectiveMs = tfr.effective?.getTime();
      const expiresMs = tfr.expires?.getTime();
      return Number.isFinite(effectiveMs) && Number.isFinite(expiresMs) && now >= tfr.effective && now <= tfr.expires;
    })
    .sort((a, b) => a.distance - b.distance);

  if (activeNearby.length === 0) return sourceResult('tfr', 'go', []);
  const nearest = activeNearby[0];
  const status = activeNearby.some(tfr => tfr.distance <= 5) ? 'no-go' : 'caution';
  const reason = status === 'no-go'
    ? `${nearest.name} is within ${nearest.distance.toFixed(1)} NM`
    : `${nearest.name || 'Nearest TFR'} is ${nearest.distance.toFixed(1)} NM away`;
  return sourceResult('tfr', status, [reason]);
}

export async function checkAirspace({ lat, lon }) {
  const res = await fetch(`/API/check-airspacev2.php?lat=${lat}&lon=${lon}&radius=${SEARCH_RADIUS_M}&type=asp`);
  if (!res.ok) throw new Error('Airspace data unavailable');
  const data = await res.json();
  const rawResults = Array.isArray(data.airspace)
    ? data.airspace
    : Array.isArray(data.asp?.features)
    ? data.asp.features
    : [];
  const results = rawResults.map(normalizeAirspaceResult);
  if (results.length === 0) return sourceResult('airspace', 'go', []);

  const overhead = results.filter(r => r.relation === 'overhead');
  const isControlled = z => ['B', 'C', 'D', 'E'].includes((z.cls || '').toUpperCase());
  const floorFt = z => {
    const floor = Number(z.alt_bottom ?? 0);
    return Number.isFinite(floor) ? floor : 0;
  };
  const floorLabel = z => {
    const floor = floorFt(z);
    return floor <= 0 ? 'Surface' : `${floor} ft ${z.alt_type || 'AGL'}`;
  };
  const controlledOverhead = overhead.filter(isControlled);

  if (controlledOverhead.length === 0) return sourceResult('airspace', 'go', []);
  const zone = [...controlledOverhead].sort((a, b) => floorFt(a) - floorFt(b))[0];
  if (floorFt(zone) > DRONE_MAX_FT) return sourceResult('airspace', 'go', []);
  return sourceResult('airspace', 'no-go', [`Class ${zone.cls} airspace at ${floorLabel(zone)} - authorization required`]);
}

export async function checkHazards({ lat, lon }) {
  const protectedAreasPromise = fetch(`/API/protected-areas.php?lat=${lat}&lon=${lon}&radius=1000`)
    .then(res => {
      if (!res.ok) throw new Error('Protected area data unavailable');
      return res.json();
    });
  const res = await fetch(`/API/check-airspacev2.php?lat=${lat}&lon=${lon}&radius=5000&type=all`);
  if (!res.ok) throw new Error('Hazard data unavailable');
  const data = await res.json();

  let partialDataError = false;
  const obstacles = data.obs?.features || [];
  const obstacleList = obstacles.map(feat => {
    const elevation = parseInt(feat.properties?.elevation_agl ?? feat.properties?.elevation ?? 0, 10);
    const distanceNm = (feat.properties?.distance_m ?? 0) / METERS_PER_NM;
    return {
      type: 'Obstacle',
      distance: distanceNm,
      severity: elevation <= DRONE_MAX_FT && distanceNm <= NEAR_OBSTACLE_ADVISORY_NM ? 'advisory' : 'info',
      reason: `${elevation} ft AGL obstacle is ${(distanceNm * 1.15078).toFixed(1)} miles away`,
    };
  });

  const restrictedList = (data.raa?.features || []).map(feat => {
    const distanceNm = (feat.properties?.distance_m ?? 0) / METERS_PER_NM;
    return {
      type: 'Restricted Area',
      distance: distanceNm,
      severity: distanceNm <= NEAR_NO_GO_CAUTION_NM ? 'caution' : 'advisory',
      reason: `${feat.properties?.name || 'Restricted area'} is ${(distanceNm * 1.15078).toFixed(1)} miles away and may limit drone operations`,
    };
  });

  const airportList = (data.apt?.features || [])
    .filter(apt => (apt.properties?.distance_m ?? Infinity) / 1609.34 < 3)
    .map(apt => {
      const distanceNm = (apt.properties?.distance_m ?? 0) / METERS_PER_NM;
      return {
        type: 'Airport',
        distance: distanceNm,
        severity: 'advisory',
        reason: `${apt.properties?.name || 'Airport/heliport'} is ${(distanceNm * 1.15078).toFixed(1)} miles away`,
      };
    });

  let protectedList = [];
  try {
    const protectedData = await protectedAreasPromise;
    protectedList = (protectedData.protectedAreas || [])
      .map(area => {
        const distance = distanceFromProtectedAreaNm(area, lat, lon);
        const severity = protectedAreaImpact(area, distance);
        return {
          type: area.category || 'Protected Area',
          distance,
          severity,
          reason: `${area.name || 'Protected area'} ${distance === 0 ? 'contains this location' : `is ${(distance * 1.15078).toFixed(1)} miles away`} - ${area.rule || 'verify local park rules'}`,
        };
      })
      .filter(area => area.severity !== 'info' || area.distance <= NEAR_NO_GO_ADVISORY_NM);
  } catch {
    partialDataError = true;
  }

  const hazards = [...protectedList, ...obstacleList, ...restrictedList, ...airportList];
  const impactHazards = hazards.filter(hazard => ['no-go', 'caution', 'advisory'].includes(hazard.severity));
  let status = 'go';
  if (protectedList.some(area => area.severity === 'no-go')) status = 'no-go';
  else if (hazards.some(hazard => hazard.severity === 'caution')) status = 'caution';
  else if (impactHazards.length > 0 || partialDataError) status = 'advisory';

  const reasons = partialDataError && impactHazards.length === 0
    ? ['Hazard data partially unavailable']
    : impactHazards
      .sort((a, b) => impactRank(b.severity) - impactRank(a.severity) || a.distance - b.distance)
      .slice(0, 3)
      .map(hazard => hazard.reason);

  return sourceResult('hazards', status, reasons);
}

export const flightCheckSources = {
  weather: checkWeather,
  tfr: checkTfr,
  airspace: checkAirspace,
  hazards: checkHazards,
};
